import { faCheck, faTriangleExclamation, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';

import {
    CAPABILITY_WRITES_KEY,
    PROBE_STATUS_ERROR,
    PROBE_STATUS_OK,
    parseJsonStringObject,
    type AlphaplanConnectionTestResult,
} from './types.ts';
import { Alert } from '../../../core/components/Alert.tsx';
import Button from '../../../core/components/Button.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import Modal from '../../../core/components/Modal.tsx';
import classnames from '../../../core/utils/classnames.tsx';

const OkIcon = withIcon(faCheck);
const WarningIcon = withIcon(faTriangleExclamation);
const FailedIcon = withIcon(faXmark);

export interface AlphaplanConnectionTestModalProps {
    integrationName: string;
    result: AlphaplanConnectionTestResult;
    onClose: () => void;
}

const ProbeStatusIcon = ({ status }: { status: string }) => {
    if (status === PROBE_STATUS_OK) {
        return (
            <span className="text-content-success-strong">
                <OkIcon />
            </span>
        );
    }
    if (status === PROBE_STATUS_ERROR) {
        return (
            <span className="text-content-danger-strong">
                <FailedIcon />
            </span>
        );
    }
    return (
        <span className="text-content-warning-strong">
            <WarningIcon />
        </span>
    );
};

const ProbeList = ({ probes }: { probes: Record<string, any> }) => {
    const entries = Object.entries(probes);
    if (entries.length === 0) return null;

    return (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
            {entries.map(([name, status]) => (
                <li key={name} className="flex gap-2 items-center text-sm">
                    <ProbeStatusIcon status={String(status)} />
                    <code className="text-content-strong">{name}</code>
                </li>
            ))}
        </ul>
    );
};

const MessageList = ({ title, messages, className }: { title: string; messages: string[]; className: string }) => {
    if (!messages?.length) return null;
    return (
        <div className="flex flex-col gap-1">
            <span className={classnames('text-sm font-semibold', className)}>{title}</span>
            <ul className="list-disc pl-4 text-sm text-content-medium">
                {messages.map((message) => (
                    <li key={message}>{message}</li>
                ))}
            </ul>
        </div>
    );
};

/** Result of `testAlphaplanIntegrationConnection`: verdict, messages, server details and the probe checklist. */
const AlphaplanConnectionTestModal = ({ integrationName, result, onClose }: AlphaplanConnectionTestModalProps) => {
    const { t } = useTranslation('integrations');

    const probes = parseJsonStringObject(result.probes);
    const capabilities = parseJsonStringObject(result.capabilities);
    const isWriteUnverified = capabilities[CAPABILITY_WRITES_KEY] === 'unverified';

    const details = [
        { label: t('alphaplan.connectionTest.version'), value: result.version },
        { label: t('alphaplan.connectionTest.licence'), value: result.licence },
        { label: t('alphaplan.connectionTest.fullname'), value: result.fullname },
        { label: t('alphaplan.connectionTest.batchMax'), value: result.batchMax },
    ].filter((detail) => detail.value !== null && detail.value !== undefined && detail.value !== '');

    return (
        <Modal
            title={t('alphaplan.connectionTest.title', { name: integrationName })}
            onClose={onClose}
            buttons={<Button onClick={onClose}>{t('alphaplan.connectionTest.closeButton')}</Button>}
        >
            <div className="flex flex-col gap-4">
                <Alert severity={result.ok ? 'success' : 'error'}>
                    {result.ok ? t('alphaplan.connectionTest.ok') : t('alphaplan.connectionTest.failed')}
                </Alert>

                <MessageList
                    title={t('alphaplan.connectionTest.errors')}
                    messages={result.errors}
                    className="text-content-danger-strong"
                />
                <MessageList
                    title={t('alphaplan.connectionTest.warnings')}
                    messages={result.warnings}
                    className="text-content-warning-strong"
                />

                {details.length > 0 && (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                        {details.map((detail) => (
                            <div key={detail.label} className="contents">
                                <dt className="text-content-medium">{detail.label}</dt>
                                <dd className="text-content-strong">{String(detail.value)}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                {Object.keys(probes).length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="text-sm font-semibold text-content-strong">
                            {t('alphaplan.connectionTest.probes')}
                        </span>
                        <ProbeList probes={probes} />
                    </div>
                )}

                {isWriteUnverified && (
                    <p className="text-sm text-content-medium">{t('alphaplan.connectionTest.writesUnverified')}</p>
                )}
            </div>
        </Modal>
    );
};

export default AlphaplanConnectionTestModal;
